import { useEffect, type ReactNode } from "react";

/**
 * Globale premium smooth-scroll:
 * - Lenis stuurt de document-scroll (imperatief, geen wrapper-component
 *   zodat de kinderen niet opnieuw mounten).
 * - Lenis's rAF wordt gedreven door gsap.ticker zodat elke scroll-driven
 *   animatie (ScrollTrigger, Framer Motion useScroll, etc.) frame-synchroon
 *   met Lenis loopt en er geen layout-jank ontstaat.
 * - Respecteert prefers-reduced-motion: dan geen Lenis, native scroll blijft.
 * - gsap + lenis worden pas ná de eerste paint dynamisch geladen: ze zitten
 *   dus niet in de initiële JS-bundel (betere LCP/TBT).
 */
export function SmoothScrollProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let disposed = false;
    let cleanup: (() => void) | undefined;

    const start = async () => {
      const [{ default: Lenis }, { gsap }, { ScrollTrigger }] = await Promise.all([
        import("lenis"),
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (disposed) return;

      gsap.registerPlugin(ScrollTrigger);

      const lenis = new Lenis({
        duration: 1.15,
        smoothWheel: true,
        lerp: 0.1,
        // autoRaf uit — wij drijven de rAF via gsap.ticker.
        autoRaf: false,
      });

      const update = (time: number) => lenis.raf(time * 1000);
      gsap.ticker.add(update);
      gsap.ticker.lagSmoothing(0);

      const onScroll = () => ScrollTrigger.update();
      lenis.on("scroll", onScroll);

      cleanup = () => {
        gsap.ticker.remove(update);
        lenis.off("scroll", onScroll);
        lenis.destroy();
      };
    };

    const idle = (window as unknown as {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    }).requestIdleCallback;
    const handle = idle ? idle(() => void start(), { timeout: 2000 }) : window.setTimeout(() => void start(), 300);

    return () => {
      disposed = true;
      if (!idle) window.clearTimeout(handle as number);
      cleanup?.();
    };
  }, []);

  return <>{children}</>;
}